import React, { useRef, useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { createShopType, editShopType, getShopTypes } from '../../apollo'
import ConfigurableValues from '../../config/constants'
import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import FormCardHeader from '../FormCardHeader'
import {
  Box,
  Typography,
  Input,
  Button,
  Alert,
  Grid,
  FormControlLabel,
  Switch
} from '@mui/material'

const CREATE_SHOP_TYPE = gql`
  ${createShopType}
`
const EDIT_SHOP_TYPE = gql`
  ${editShopType}
`
const GET_SHOP_TYPES = gql`
  ${getShopTypes}
`

// Quick-pick icons for the shop type tiles in the customer app
const EMOJI_SUGGESTIONS = [
  '🍔',
  '🛒',
  '💊',
  '🏪',
  '🐾',
  '🌸',
  '🎁',
  '🍷',
  '🥛',
  '🍞',
  '📦',
  '🧴'
]

// How a store of this type lays out its products in the customer app
const DISPLAY_VARIANTS = [
  {
    value: 'row',
    labelKey: 'VariantRow',
    descriptionKey: 'VariantRowHelp'
  },
  {
    value: 'grid',
    labelKey: 'VariantGrid',
    descriptionKey: 'VariantGridHelp'
  }
]

// Restaurants store this value in `shopType`, so it has to stay url friendly
const slugify = value =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// Tiny wireframe of the store page so both product layouts can be compared
const VariantPreview = ({ variant }) => {
  const block = {
    backgroundColor: '#F0F0F0',
    border: '1px solid #DCDCDC',
    borderRadius: 4
  }
  const line = { backgroundColor: '#DCDCDC', borderRadius: 2, height: 5 }

  if (variant === 'row') {
    return (
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {[0, 1, 2].map(index => (
          <Box
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: 5,
              ...block
            }}>
            <Box style={{ flex: 1 }}>
              <Box style={{ ...line, width: '70%' }} />
              <Box style={{ ...line, width: '35%', marginTop: 4 }} />
            </Box>
            <Box
              style={{
                width: 22,
                height: 22,
                backgroundColor: '#DCDCDC',
                borderRadius: 4
              }}
            />
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 6
      }}>
      {[0, 1, 2, 3].map(index => (
        <Box key={index} style={{ padding: 5, ...block }}>
          <Box
            style={{
              height: 20,
              backgroundColor: '#DCDCDC',
              borderRadius: 4
            }}
          />
          <Box style={{ ...line, width: '80%', marginTop: 4 }} />
          <Box style={{ ...line, width: '40%', marginTop: 3 }} />
        </Box>
      ))}
    </Box>
  )
}

function ShopType(props) {
  const { t, shopType } = props
  const mutation = shopType ? EDIT_SHOP_TYPE : CREATE_SHOP_TYPE
  const { CLOUDINARY_UPLOAD_URL, CLOUDINARY_FOOD } = ConfigurableValues()
  const fileInputRef = useRef()

  const [title, setTitle] = useState(shopType ? shopType.title : '')
  const [slug, setSlug] = useState(shopType ? shopType.slug : '')
  // Once the record exists the slug is locked to what restaurants already store
  const [slugTouched, setSlugTouched] = useState(Boolean(shopType))
  const [description, setDescription] = useState(
    shopType ? shopType.description || '' : ''
  )
  const [image, setImage] = useState(shopType ? shopType.image || '' : '')
  const [emoji, setEmoji] = useState(shopType ? shopType.emoji || '' : '')
  const [order, setOrder] = useState(
    shopType && shopType.order !== null && shopType.order !== undefined
      ? String(shopType.order)
      : '0'
  )
  const [isActive, setIsActive] = useState(
    shopType ? shopType.isActive !== false : true
  )
  const [displayVariant, setDisplayVariant] = useState(
    shopType && shopType.displayVariant === 'grid' ? 'grid' : 'row'
  )

  const [imageLoading, setImageLoading] = useState(false)

  const [mainError, mainErrorSetter] = useState('')
  const [success, successSetter] = useState('')
  const [titleError, setTitleError] = useState(null)

  const clearFields = () => {
    setTitle('')
    setSlug('')
    setSlugTouched(false)
    setDescription('')
    setImage('')
    setEmoji('')
    setOrder('0')
    setIsActive(true)
    setDisplayVariant('row')
    setTitleError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Only one image per shop type: picking a new file replaces whatever was set
  // before (uploaded file or pasted url)
  const onSelectImage = event => {
    const picked = Array.from(event.target.files || []).find(file =>
      file.name.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)
    )
    // Let the same file be picked again after a remove
    event.target.value = ''
    if (!picked) return

    // Roomy enough for the full width artwork the home screen tiles now show
    if (picked.size > 5 * 1024 * 1024) {
      mainErrorSetter('File size should be less than or equal to 5MB')
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      mainErrorSetter('')
      setImage(reader.result)
    }
    reader.readAsDataURL(picked)
  }

  const removeImage = () => {
    setImage('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Base64 previews get pushed to cloudinary, plain urls are kept as they are
  const uploadImageToCloudinary = async () => {
    if (!image) return ''
    if (!image.startsWith('data:')) return image.trim()

    setImageLoading(true)
    try {
      const result = await fetch(`${CLOUDINARY_UPLOAD_URL}/image/upload`, {
        body: JSON.stringify({ file: image, upload_preset: CLOUDINARY_FOOD }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST'
      })
      const imageData = await result.json()
      if (!imageData.secure_url) throw new Error('No url returned')
      return imageData.secure_url
    } catch (e) {
      console.error('Shop type image upload error => ', e)
      mainErrorSetter('Failed to upload image to Cloudinary.')
      return null
    } finally {
      setImageLoading(false)
    }
  }

  const onCompleted = () => {
    successSetter(shopType ? t('ShopTypeUpdated') : t('ShopTypeAdded'))
    mainErrorSetter('')
    if (!shopType) clearFields()
  }

  const onError = error => {
    let message = ''
    try {
      message = error.graphQLErrors[0].message
    } catch (err) {
      message = t('ActionFailedTryAgain')
    }
    successSetter('')
    mainErrorSetter(message)
  }

  const [mutate, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_SHOP_TYPES }],
    onError,
    onCompleted
  })

  const onTitleChange = value => {
    setTitle(value)
    if (!slugTouched) setSlug(slugify(value))
  }

  const onSubmitValidation = () => {
    const valid = title.trim().length > 0 && slugify(slug).length > 0
    setTitleError(valid)
    return valid
  }

  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  return (
    <Box container className={classes.container}>
      <FormCardHeader
        className={classes.heading}
        title={shopType ? t('EditShopType') : t('AddShopType')}
        onClose={props.onClose}
      />
      <Box className={classes.form}>
        <form>
          <Box className={globalClasses.flexRow}>
            <Grid container columnSpacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.labelText}>
                  {t('Name')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-shop-type-title"
                  name="input-shop-type-title"
                  placeholder={t('Name')}
                  type="text"
                  value={title}
                  onChange={event => onTitleChange(event.target.value)}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    titleError === false
                      ? globalClasses.inputError
                      : titleError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.labelText}>
                  {t('Description')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-shop-type-description"
                  name="input-shop-type-description"
                  placeholder={t('Description')}
                  type="text"
                  value={description}
                  onChange={event => setDescription(event.target.value)}
                  disableUnderline
                  className={globalClasses.input}
                />
                <Typography
                  className={classes.labelText}
                  style={{ opacity: 0.7, fontSize: 13 }}>
                  {t('ShopTypeDescriptionHelp')}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box className={globalClasses.flexRow}>
            <Grid container columnSpacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.labelText}>
                  {t('Slug')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-shop-type-slug"
                  name="input-shop-type-slug"
                  placeholder={t('PHShopTypeSlug')}
                  type="text"
                  value={slug}
                  onChange={event => {
                    setSlugTouched(true)
                    setSlug(event.target.value)
                  }}
                  onBlur={event => setSlug(slugify(event.target.value))}
                  disableUnderline
                  className={globalClasses.input}
                />
                {/* Spaces, punctuation and capitals are stripped on save, which
                    is baffling if it only happens after the field loses focus.
                    Showing the saved form as it is typed makes it expected. */}
                {slugify(slug) !== slug.trim() && slug.trim() ? (
                  <Typography
                    className={classes.labelText}
                    style={{ opacity: 0.9, fontSize: 13 }}>
                    {t('ShopTypeSlugPreview')} <b>{slugify(slug)}</b>
                  </Typography>
                ) : null}
                <Typography
                  className={classes.labelText}
                  style={{ opacity: 0.7, fontSize: 13 }}>
                  {t('ShopTypeSlugHelp')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.labelText}>
                  {t('DisplayOrder')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-shop-type-order"
                  name="input-shop-type-order"
                  placeholder="0"
                  type="number"
                  value={order}
                  onChange={event => setOrder(event.target.value)}
                  disableUnderline
                  className={globalClasses.input}
                />
              </Grid>
            </Grid>
          </Box>

          <Box className={globalClasses.flexRow}>
            <Grid container columnSpacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.labelText}>
                  {t('ImageUrl')} ({t('Optional')})
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-shop-type-image"
                  name="input-shop-type-image"
                  placeholder={t('PHShopTypeImage')}
                  type="text"
                  value={image.startsWith('data:') ? '' : image}
                  onChange={event => setImage(event.target.value)}
                  disabled={image.startsWith('data:')}
                  disableUnderline
                  className={globalClasses.input}
                />
                <Typography
                  className={classes.labelText}
                  style={{ opacity: 0.7, fontSize: 13 }}>
                  {t('ShopTypeImageHelp')}
                </Typography>
                <Box
                  mt={1}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginLeft: '4%'
                  }}>
                  {image ? (
                    <img
                      key={image}
                      className={classes.image}
                      alt=""
                      src={image}
                      onError={event => {
                        event.target.style.visibility = 'hidden'
                      }}
                    />
                  ) : null}
                  <Box>
                    <label
                      htmlFor={
                        shopType
                          ? `edit-shop-type-image-${shopType._id}`
                          : 'add-shop-type-image'
                      }
                      className={classes.fileUpload}>
                      {t('UploadAnImage')}
                    </label>
                    <input
                      ref={fileInputRef}
                      className={classes.file}
                      id={
                        shopType
                          ? `edit-shop-type-image-${shopType._id}`
                          : 'add-shop-type-image'
                      }
                      type="file"
                      accept="image/*"
                      onChange={onSelectImage}
                    />
                    {image ? (
                      <Button
                        type="button"
                        onClick={removeImage}
                        style={{
                          marginLeft: 8,
                          fontSize: 12,
                          textTransform: 'none'
                        }}>
                        {t('Remove')}
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.labelText}>
                  {t('Emoji')} ({t('Optional')})
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-shop-type-emoji"
                  name="input-shop-type-emoji"
                  placeholder={t('PHShopTypeEmoji')}
                  type="text"
                  value={emoji}
                  onChange={event => setEmoji(event.target.value.trim())}
                  disableUnderline
                  className={globalClasses.input}
                />
                <Box
                  mt={1}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    alignItems: 'center'
                  }}>
                  {EMOJI_SUGGESTIONS.map(suggestion => (
                    <Button
                      key={suggestion}
                      type="button"
                      onClick={() =>
                        setEmoji(current =>
                          current === suggestion ? '' : suggestion
                        )
                      }
                      style={{
                        minWidth: 36,
                        height: 36,
                        padding: 0,
                        fontSize: 18,
                        lineHeight: '36px',
                        borderRadius: 8,
                        border:
                          emoji === suggestion
                            ? '2px solid #F6985D'
                            : '1px solid #E0E0E0',
                        backgroundColor:
                          emoji === suggestion ? '#FFF3E9' : 'transparent'
                      }}>
                      {suggestion}
                    </Button>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box className={globalClasses.flexRow} mt={2}>
            <Grid container columnSpacing={3}>
              <Grid item xs={12}>
                <Typography className={classes.labelText}>
                  {t('HomeLayout')}
                </Typography>
                <Box
                  mt={1}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {DISPLAY_VARIANTS.map(variant => {
                    const selected = displayVariant === variant.value
                    return (
                      <Box
                        key={variant.value}
                        onClick={() => setDisplayVariant(variant.value)}
                        style={{
                          cursor: 'pointer',
                          flex: '1 1 240px',
                          padding: 12,
                          borderRadius: 10,
                          border: selected
                            ? '2px solid #F6985D'
                            : '1px solid #E0E0E0',
                          backgroundColor: selected ? '#FFF3E9' : 'transparent'
                        }}>
                        {/* Miniature of how the home screen will look */}
                        <VariantPreview variant={variant.value} />
                        <Typography style={{ fontWeight: 600, marginTop: 8 }}>
                          {t(variant.labelKey)}
                        </Typography>
                        <Typography style={{ fontSize: 13, opacity: 0.7 }}>
                          {t(variant.descriptionKey)}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box className={globalClasses.flexRow} mt={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={event => setIsActive(event.target.checked)}
                />
              }
              label={t('Status')}
              style={{ marginLeft: '4%' }}
            />
          </Box>

          {loading || imageLoading ? t('Loading') : null}
          <Box className={globalClasses.formActions}>
            <Button
              className={globalClasses.button}
              disabled={loading || imageLoading}
              onClick={async e => {
                e.preventDefault()
                if (onSubmitValidation() && !loading && !imageLoading) {
                  const uploadedImage = await uploadImageToCloudinary()
                  // Upload failed: keep the form as is so nothing is lost
                  if (uploadedImage === null) return
                  mutate({
                    variables: {
                      shopTypeInput: {
                        ...(shopType ? { _id: shopType._id } : {}),
                        title: title.trim(),
                        slug: slugify(slug),
                        description: description.trim(),
                        // Replaces the previous image, or clears it when removed
                        image: uploadedImage,
                        emoji,
                        order: Number(order) || 0,
                        isActive,
                        displayVariant
                      }
                    }
                  })
                }
              }}>
              {t('Save')}
            </Button>
          </Box>
        </form>
        <Box mt={2}>
          {success && (
            <Alert
              className={globalClasses.alertSuccess}
              variant="filled"
              severity="success">
              {success}
            </Alert>
          )}
          {mainError && (
            <Alert
              className={globalClasses.alertError}
              variant="filled"
              severity="error">
              {mainError}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default withTranslation()(ShopType)
